import React from 'react';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';

const ProfilePhoto = ({ user, profilePhoto, setProfilePhoto, setUser, formData, uploadingPhoto, setUploadingPhoto }) => {
  
  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Error',
        description: 'Please select a valid image file',
        variant: 'destructive',
      });
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: 'Error',
        description: 'Image size must be less than 2MB',
        variant: 'destructive',
      });
      return;
    }

    setUploadingPhoto(true);

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result;
        setProfilePhoto(base64Image);

        // Save to backend
        const api = (await import('../../utils/api')).default;
        await api.put('/auth/me', {
          ...formData,
          profilePhoto: base64Image,
        });

        // Update local user state
        setUser({ ...user, profilePhoto: base64Image });

        toast({
          title: 'Photo uploaded',
          description: 'Your profile photo has been updated',
        });
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload photo',
        variant: 'destructive',
      });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      await api.put('/auth/me', {
        ...formData,
        profilePhoto: null,
      });

      setProfilePhoto(null);
      setUser({ ...user, profilePhoto: null });

      toast({
        title: 'Photo removed',
        description: 'Your profile photo has been removed',
      });
    } catch (error) {
      console.error('Error removing photo:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove photo',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="flex items-center gap-6 pb-6 border-b border-gray-200 mb-6">
      <div className="relative">
        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-white text-3xl font-bold overflow-hidden">
          {(user.profilePhoto || profilePhoto) ? (
            <img 
              src={profilePhoto || user.profilePhoto} 
              alt="Profile" 
              className="w-full h-full object-cover"
            />
          ) : (
            user.name?.charAt(0).toUpperCase() || 'U'
          )}
        </div>
        {uploadingPhoto && (
          <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          </div>
        )}
      </div>
      <div className="flex-1">
        <h3 className="font-semibold text-gray-900 mb-1">Profile Photo</h3>
        <p className="text-sm text-gray-600 mb-3">Upload a photo to personalize your account</p>
        <div className="flex gap-2">
          <label htmlFor="photo-upload">
            <input
              id="photo-upload"
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              className="hidden"
              disabled={uploadingPhoto}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => document.getElementById('photo-upload').click()}
              disabled={uploadingPhoto}
              className="cursor-pointer"
            >
              {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
            </Button>
          </label>
          {(user.profilePhoto || profilePhoto) && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRemovePhoto}
              disabled={uploadingPhoto}
              className="text-red-600 hover:text-red-700"
            >
              Remove
            </Button>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-2">Max size: 2MB. Formats: JPG, PNG, GIF</p>
      </div>
    </div>
  );
};

export default ProfilePhoto;
